import { createQueryKeys } from '@lukemorales/query-key-factory';
import type { StoxlyClient } from '@stoxly/api-client';
import type { Body, Query } from '../op-types';

export function createWalletQueries(client: StoxlyClient) {
  return createQueryKeys('wallets', {
    // ['wallets', 'all']
    all: {
      queryKey: null,
      queryFn: ({ signal }) => client.walletsFindAll({ signal }),
    },

    // ['wallets', 'totalBalance', { query }]
    totalBalance: (query?: Query<'walletsGetTotalBalance'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) => client.walletsGetTotalBalance({ queries: query, signal }),
    }),

    // ['wallets', 'recentTransactions', { query }]
    recentTransactions: (query?: Query<'walletsGetRecentTransactions'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) => client.walletsGetRecentTransactions({ queries: query, signal }),
    }),

    // ['wallets', 'transactionsSummary', { query }]
    transactionsSummary: (query: Query<'walletTransactionsGetSummary'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) => client.walletTransactionsGetSummary({ queries: query, signal }),
    }),

    // ['wallets', 'detail', walletId, ...]
    detail: (walletId: string) => ({
      queryKey: [walletId],
      queryFn: ({ signal }) => client.walletsFindOne({ params: { id: walletId }, signal }),
      contextQueries: {
        transactions: (query?: Query<'walletsGetTransactions'>) => ({
          queryKey: [{ query }],
          queryFn: ({ signal }) =>
            client.walletsGetTransactions({ params: { id: walletId }, queries: query, signal }),
        }),
        transactionsExport: (query: Query<'walletsExportTransactions'>) => ({
          queryKey: [{ query }],
          queryFn: ({ signal }) =>
            client.walletsExportTransactions({ params: { id: walletId }, queries: query, signal }),
        }),
        balanceHistory: (query?: Query<'walletsGetBalanceHistory'>) => ({
          queryKey: [{ query }],
          queryFn: ({ signal }) =>
            client.walletsGetBalanceHistory({ params: { id: walletId }, queries: query, signal }),
        }),
        deletionImpact: {
          queryKey: null,
          queryFn: ({ signal }) =>
            client.walletsGetDeletionImpact({ params: { id: walletId }, signal }),
        },
        portfolioTransactionsCount: {
          queryKey: null,
          queryFn: ({ signal }) =>
            client.walletsGetPortfolioTransactionsCount({ params: { id: walletId }, signal }),
        },
      },
    }),
  });
}

export function createWalletMutations(client: StoxlyClient) {
  return {
    create: {
      mutationFn: (body: Body<'walletsCreate'>) => client.walletsCreate(body),
    },
    update: {
      mutationFn: ({ id, body }: { id: string; body: Body<'walletsUpdate'> }) =>
        client.walletsUpdate(body, { params: { id } }),
    },
    delete: {
      mutationFn: (id: string) => client.walletsDelete(undefined, { params: { id } }),
    },
    transfer: {
      mutationFn: (body: Body<'walletsTransfer'>) => client.walletsTransfer(body),
    },
    deposit: {
      mutationFn: ({ id, body }: { id: string; body: Body<'walletsDeposit'> }) =>
        client.walletsDeposit(body, { params: { id } }),
    },
    withdraw: {
      mutationFn: ({ id, body }: { id: string; body: Body<'walletsWithdraw'> }) =>
        client.walletsWithdraw(body, { params: { id } }),
    },
    updateTransaction: {
      mutationFn: ({
        walletId,
        transactionId,
        body,
      }: {
        walletId: string;
        transactionId: string;
        body: Body<'walletsUpdateTransaction'>;
      }) => client.walletsUpdateTransaction(body, { params: { walletId, transactionId } }),
    },
    deleteTransaction: {
      mutationFn: ({ walletId, transactionId }: { walletId: string; transactionId: string }) =>
        client.walletsDeleteTransaction(undefined, { params: { walletId, transactionId } }),
    },
  };
}
